/**
 * Custom hooks for leads feature
 */

import { useState, useEffect, useCallback, useRef } from "react";
import * as leadService from "../services/leadService.js";

/**
 * Hook for fetching leads with filtering and pagination
 * @param {Object} initialFilters - Initial filter criteria
 * @returns {Object} Leads data and loading state
 */
export const useLeads = (initialFilters = {}) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [count, setCount] = useState(0);

  const fetchLeads = useCallback(
    async (newFilters = {}) => {
      setLoading(true);
      setError(null);

      try {
        // Merge filters: use newFilters if provided, otherwise use current filters
        const mergedFilters = { ...filters, ...newFilters };
        
        const result = await leadService.getLeads(mergedFilters);

        if (result.success) {
          // Handle both 'data' and 'leads' response formats
          const leadsData = result.data || result.leads || [];
          setLeads(leadsData);
          setCount(result.count || leadsData.length);
          setFilters(mergedFilters);
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  // Refetch when filters change
  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Update filters and refetch
  const updateFilters = useCallback(
    (newFilters) => {
      setFilters(newFilters);
      fetchLeads(newFilters);
    },
    [fetchLeads]
  );

  return {
    leads,
    loading,
    error,
    count,
    filters,
    refetch: () => fetchLeads(),
    updateFilters,
  };
};

/**
 * Hook for fetching a single lead
 * @param {string} leadId - Lead identifier
 * @param {Object} initialLead - Initial lead data (for optimistic updates)
 * @returns {Object} Lead data and loading state
 */
export const useLead = (leadId, initialLead = null) => {
  const initialMatchesLead =
    Boolean(leadId) &&
    Boolean(initialLead) &&
    String(initialLead.id || initialLead._id || "") === String(leadId);

  const [lead, setLead] = useState(
    initialMatchesLead ? initialLead : null
  );
  // Full-page loading only when we have nothing to show yet
  const [loading, setLoading] = useState(!initialMatchesLead);
  const [error, setError] = useState(null);
  // Invalidate in-flight fetches on id change / newer refetch (avoids stale overwrite)
  const fetchGenerationRef = useRef(0);
  const leadIdRef = useRef(leadId);
  leadIdRef.current = leadId;

  const fetchLead = useCallback(async (id, { background = false } = {}) => {
    if (!id) {
      setLead(null);
      setLoading(false);
      setError("Lead ID is required");
      return;
    }

    const generation = ++fetchGenerationRef.current;

    // Keep existing lead visible during background refetch
    if (!background) {
      setLoading(true);
    }
    setError(null);

    try {
      const result = await leadService.getLead(id);
      if (
        generation !== fetchGenerationRef.current ||
        leadIdRef.current !== id
      ) {
        return;
      }

      if (result.success) {
        setLead(result.data);
      } else {
        setError(result.error || "Failed to load lead");
      }
    } catch (err) {
      if (
        generation !== fetchGenerationRef.current ||
        leadIdRef.current !== id
      ) {
        return;
      }
      setError(err.message || "Failed to load lead");
    } finally {
      if (
        generation === fetchGenerationRef.current &&
        leadIdRef.current === id
      ) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!leadId) {
      fetchGenerationRef.current += 1;
      setLead(null);
      setLoading(false);
      setError("Lead ID is required");
      return undefined;
    }

    const generation = ++fetchGenerationRef.current;
    const matchingInitial =
      initialLead &&
      String(initialLead.id || initialLead._id || "") === String(leadId)
        ? initialLead
        : null;

    if (matchingInitial) {
      setLead(matchingInitial);
      setLoading(false);
    } else {
      // Drop previous lead so we don't flash the wrong lead while the new id loads
      setLead(null);
      setLoading(true);
    }
    setError(null);

    (async () => {
      try {
        const result = await leadService.getLead(leadId);
        if (generation !== fetchGenerationRef.current) return;
        if (result.success) {
          setLead(result.data);
        } else {
          setError(result.error || "Failed to load lead");
          if (!matchingInitial) {
            setLead(null);
          }
        }
      } catch (err) {
        if (generation !== fetchGenerationRef.current) return;
        setError(err.message || "Failed to load lead");
        if (!matchingInitial) {
          setLead(null);
        }
      } finally {
        if (generation === fetchGenerationRef.current) {
          setLoading(false);
        }
      }
    })();

    return () => {
      // Bump so in-flight effect/refetch results are ignored after unmount or id change
      fetchGenerationRef.current += 1;
    };
    // Only re-fetch when the route id changes; initialLead is read for that id
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: avoid refetch loops from new location.state object identity
  }, [leadId]);

  return {
    lead,
    loading,
    error,
    refetch: () => fetchLead(leadId, { background: Boolean(lead) }),
  };
};

/**
 * Hook for lead statistics
 * @returns {Object} Statistics data and loading state
 */
export const useLeadStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await leadService.getLeadStats();

      if (result.success) {
        setStats(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  };
};

/**
 * Hook for updating lead status
 * @returns {Object} Update function and loading state
 */
export const useUpdateLeadStatus = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateStatus = useCallback(async (leadId, updateData) => {
    setLoading(true);
    setError(null);

    try {
      const result = await leadService.updateLeadStatus(leadId, updateData);

      if (!result.success) {
        setError(result.error);
        return result;
      }

      return result;
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    updateStatus,
    loading,
    error,
  };
};

/**
 * Hook for updating lead information
 * @returns {Object} Update function and loading state
 */
export const useUpdateLeadInfo = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateInfo = useCallback(async (leadId, updateData) => {
    setLoading(true);
    setError(null);

    try {
      const result = await leadService.updateLeadInfo(leadId, updateData);

      if (!result.success) {
        setError(result.error);
        return result;
      }

      return result;
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    updateInfo,
    loading,
    error,
  };
};

/**
 * Hook for converting lead to quote
 * @returns {Object} Convert function and loading state
 */
export const useConvertLeadToQuote = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const convertToQuote = useCallback(
    async (leadId, leadData = null, conversionData = {}) => {
      setLoading(true);
      setError(null);

      try {
        const result = await leadService.convertLeadToQuote(
          leadId,
          leadData,
          conversionData
        );

        if (!result.success) {
          setError(result.error);
          return result;
        }

        return result;
      } catch (err) {
        setError(err.message);
        return { success: false, error: err.message };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    convertToQuote,
    loading,
    error,
  };
};
